import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatArea.css';
import MessageInput from './MessageInput';
import NoteEditor from './NoteEditor';
import { rsaDecrypt, aesEncrypt, aesDecrypt } from '../utils/crypto';
import { publicChatAPI, createWebSocketConnection } from '../utils/api';

const ChatArea = ({ currentChat, userKeys, serverPublicKey }) => {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const [filterSettings, setFilterSettings] = useState({
    enabled: false,
    password: '',
    tag: '',
    onlyMessageTag: false
  });
  const filterSettingsRef = useRef({
    enabled: false,
    password: '',
    tag: '',
    onlyMessageTag: false
  });
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // 初始化过滤设置
  useEffect(() => {
    // 从本地存储加载过滤设置
    const enabled = localStorage.getItem('toxchat_aes_enabled') === 'true';
    const password = localStorage.getItem('toxchat_aes_password') || '';
    const tag = localStorage.getItem('toxchat_aes_tag') || '';
    const onlyMessageTag = localStorage.getItem('toxchat_only_message_tag') === 'true';
    
    const initialSettings = {
      enabled: enabled && password && tag,
      password: enabled ? password : '',
      tag: enabled ? tag : '',
      onlyMessageTag: onlyMessageTag
    };
    
    setFilterSettings(initialSettings);
    filterSettingsRef.current = initialSettings;
  }, []);

  // 加载消息数据
  useEffect(() => {
    loadMessages();
  }, [currentChat]); // eslint-disable-line react-hooks/exhaustive-deps

  // 当过滤设置改变时，重新解密所有消息
  useEffect(() => {
    if (messages.length > 0) {
      // 使用 useMemo 缓存正则表达式
      const tagPatternCache = new Map();
      
      setMessages(prevMessages => 
        prevMessages.map(msg => {
           // 只处理自定义AES加密的消息
           if (msg.isCustomAES && msg.customTag) {
             const tag = msg.customTag;
             
             // 缓存正则表达式，避免重复创建
             let tagPattern = tagPatternCache.get(tag);
             if (!tagPattern) {
               tagPattern = new RegExp(`\\[${tag}\\]|\\(${tag}\\)|\\[标签:${tag}\\]|\\[${tag}\\]\\s*\\[${tag}\\]|\\(${tag}\\)\\s*\\(${tag}\\)$`);
               tagPatternCache.set(tag, tagPattern);
             }
             
             const aesEncryptedContent = msg.content.replace(tagPattern, '').trim();
            
            // 检查是否有匹配的过滤设置
            if (filterSettingsRef.current.enabled && 
                filterSettingsRef.current.tag === msg.customTag && 
                filterSettingsRef.current.password) {
              // 尝试用设置的密码解密
              const aesDecrypted = aesDecrypt(aesEncryptedContent, filterSettingsRef.current.password);
              if (aesDecrypted) {
                return { ...msg, content: aesDecrypted };
              } else {
                return { ...msg, content: `${aesEncryptedContent} [Tag: ${msg.customTag}]` };
              }
            } else {
              // 没有匹配的密码设置，显示加密内容
              return { ...msg, content: `${aesEncryptedContent} [Tag: ${msg.customTag}]` };
            }
          }
          return msg;
        })
      );
    }
  }, [filterSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMessages = async () => {
    try {
      if (currentChat === 'public' && userKeys && userKeys.userId) {
        const response = await publicChatAPI.getMessages(userKeys.userId, 0, 50, userKeys);
        
        if (response && response.success) {
          if (response.messages && Array.isArray(response.messages)) {
            // 将消息数组反转，让最新消息显示在底部
            const reversedMessages = response.messages.reverse();
            const processedMessages = reversedMessages.map(msg => {
              // 解密历史消息
              let decryptedContent = msg.content_encrypted;
              try {
                const userDecrypted = userKeys ? rsaDecrypt(msg.content_encrypted, userKeys.privateKey) : null;
                if (userDecrypted) {
                  decryptedContent = userDecrypted;
                  
                   // 如果使用了自定义AES加密，尝试自动解密
                   if (msg.is_custom_aes && msg.custom_tag) {
                     // 支持多种标签格式：[tag], (tag), [标签:tag], [tag] [tag], (tag) (tag)
                     const tag = msg.custom_tag;
                     const tagPattern = new RegExp(`\\[${tag}\\]|\\(${tag}\\)|\\[标签:${tag}\\]|\\[${tag}\\]\\s*\\[${tag}\\]|\\(${tag}\\)\\s*\\(${tag}\\)$`);
                     const aesEncryptedContent = decryptedContent.replace(tagPattern, '').trim();
                    
                    // 检查是否有匹配的过滤设置（相同的标签和密码）
                    if (filterSettingsRef.current.enabled && 
                        filterSettingsRef.current.tag === msg.custom_tag && 
                        filterSettingsRef.current.password) {
                      // 尝试用设置的密码解密
                      const aesDecrypted = aesDecrypt(aesEncryptedContent, filterSettingsRef.current.password);
                      if (aesDecrypted) {
                        decryptedContent = aesDecrypted;
                      } else {
                        decryptedContent = `${aesEncryptedContent} [Tag: ${msg.custom_tag}]`;
                      }
                    } else {
                      // 没有匹配的密码设置，显示加密内容
                      decryptedContent = `${aesEncryptedContent} [Tag: ${msg.custom_tag}]`;
                    }
                  }
                }
              } catch (error) {
              }

              return {
                id: msg.message_id,
                userId: msg.user_id,
                content: decryptedContent,
                timestamp: msg.created_at,
                encrypted: msg.is_custom_aes,
                isCustomAES: msg.is_custom_aes,
                customTag: msg.custom_tag
              };
            });
            
            setMessages(processedMessages);
            
            // 消息加载完成后强制修复布局
            setTimeout(() => {
              setLayoutKey(prev => prev + 1);
              const appElement = document.querySelector('.app');
              if (appElement) {
                appElement.style.transform = 'translateZ(0)';
                setTimeout(() => {
                  appElement.style.transform = '';
                }, 0);
              }
            }, 200);
          } else {
            setMessages([]);
          }
        } else {
          setMessages([]);
        }
      } else if (currentChat && currentChat !== 'public' && !currentChat.startsWith('note_')) {
        // 私聊消息加载逻辑
        // 这里需要先获取或创建聊天会话
      }
    } catch (error) {
      
      // 如果是网络错误或JSON解析错误，尝试重试
      if (error.message.includes('JSON解析失败') || error.message.includes('API响应为空')) {
        setTimeout(() => {
          loadMessages();
        }, 3000);
      } else {
        setMessages([]);
      }
    }
  };

  // 滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    // 消息加载完成后强制重新计算布局
    setTimeout(() => {
      const appElement = document.querySelector('.app');
      if (appElement) {
        appElement.style.transform = 'translateZ(0)';
        setTimeout(() => {
          appElement.style.transform = '';
        }, 0);
      }
    }, 100);
  }, [messages]);

  // WebSocket连接
  useEffect(() => {
    if (userKeys && userKeys.accessToken) {
      try {
        const ws = createWebSocketConnection(
          userKeys,
          handleWebSocketMessage,
          handleWebSocketError,
          handleWebSocketClose
        );
        wsRef.current = ws;
      } catch (error) {
        setIsConnected(false);
      }
    } else {
      setIsConnected(false);
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [userKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWebSocketMessage = (message) => {
    // 处理不同类型的WebSocket消息
    if (message.type === 'welcome') {
      setIsConnected(true);
    } else if (message.type === 'public_message') {
      try {
        // 安全地处理时间戳，使用UTC时间
        let timestamp;
        if (message.data.created_at) {
          const date = new Date(message.data.created_at);
          if (isNaN(date.getTime())) {
            timestamp = new Date().toISOString(); // 使用UTC时间
          } else {
            timestamp = date.toISOString(); // 使用UTC时间
          }
        } else {
          timestamp = new Date().toISOString(); // 使用UTC时间
        }

        // 解密消息内容
        let decryptedContent = message.data.content_encrypted;
        if (message.data.content_encrypted) {
          try {
            // 用用户自己的私钥解密
            const userDecrypted = userKeys ? rsaDecrypt(message.data.content_encrypted, userKeys.privateKey) : null;
            if (userDecrypted) {
              decryptedContent = userDecrypted;
              
               // 如果使用了自定义AES加密，尝试自动解密
               if (message.data.is_custom_aes && message.data.custom_tag) {
                 // 提取AES加密的内容（去掉标签部分）
                 // 支持多种标签格式：[tag], (tag), [标签:tag], [tag] [tag], (tag) (tag)
                 const tag = message.data.custom_tag;
                 const tagPattern = new RegExp(`\\[${tag}\\]|\\(${tag}\\)|\\[标签:${tag}\\]|\\[${tag}\\]\\s*\\[${tag}\\]|\\(${tag}\\)\\s*\\(${tag}\\)$`);
                 const aesEncryptedContent = decryptedContent.replace(tagPattern, '').trim();
                
                 // 检查是否有匹配的过滤设置（相同的标签和密码）
                 if (filterSettingsRef.current.enabled && 
                     filterSettingsRef.current.tag === message.data.custom_tag && 
                     filterSettingsRef.current.password) {
                   // 尝试用设置的密码解密
                   const aesDecrypted = aesDecrypt(aesEncryptedContent, filterSettingsRef.current.password);
                   if (aesDecrypted) {
                     decryptedContent = aesDecrypted;
                   } else {
                     decryptedContent = `${aesEncryptedContent} [Tag: ${message.data.custom_tag}]`;
                   }
                 } else {
                   // 没有匹配的密码设置，显示加密内容
                   decryptedContent = `${aesEncryptedContent} [Tag: ${message.data.custom_tag}]`;
                 }
              }
            } else {
            }
          } catch (error) {
            decryptedContent = message.data.content_encrypted;
          }
        }

        // 过滤掉自己发送的消息（避免重复显示）
        if (userKeys && message.data.user_id !== userKeys.userId) {
          const newMessage = {
            id: message.data.message_id,
            userId: message.data.user_id,
            content: decryptedContent,
            timestamp: timestamp,
            encrypted: message.data.is_custom_aes,
            isCustomAES: message.data.is_custom_aes,
            customTag: message.data.custom_tag
          };
          
          setMessages(prev => [...prev, newMessage]);
        }
      } catch (error) {
      }
    }
  };

  const handleWebSocketError = (error) => {
    setIsConnected(false);
  };

  const handleWebSocketClose = () => {
    setIsConnected(false);
  };

  // 处理过滤设置变化
  const handleFilterChange = useCallback((newFilterSettings) => {
    // 同时更新状态和ref
    setFilterSettings(newFilterSettings);
    filterSettingsRef.current = newFilterSettings;
  }, []);

  const handleSendMessage = async (messageData) => {
    const { content, useCustomAES, aesPassword, customTag } = messageData;
    let finalContent = content;
    let isCustomAES = false;
    
    try {
      // 如果使用自定义AES加密
      if (useCustomAES && aesPassword) {
        const encryptedContent = aesEncrypt(content, aesPassword);
        if (encryptedContent) {
          finalContent = `${encryptedContent}[${customTag || ''}]`;
          isCustomAES = true;
        }
      }

      // 发送到服务器
      if (currentChat === 'public' && userKeys && userKeys.userId) {
        const response = await publicChatAPI.sendMessage({
          encrypted_message: finalContent,
          user_id: userKeys.userId,
          timestamp: Date.now(), // 使用UTC时间戳
          is_custom_aes: isCustomAES,
          custom_tag: isCustomAES ? customTag : null
        }, userKeys);

        if (response.success) {
          // 创建本地消息显示
          const newMessage = {
            id: response.message_id || `temp_${Date.now()}_${Math.random()}`,
            userId: userKeys ? userKeys.userId : null,
            content: finalContent,
            timestamp: new Date().toISOString(), // 使用UTC时间
            encrypted: isCustomAES,
            isCustomAES,
            customTag: isCustomAES ? customTag : null,
            isOwn: true
          };
          setMessages(prev => [...prev, newMessage]);
        } else {
          alert('发送消息失败: ' + response.message);
        }
      } else {
        // 私聊消息发送逻辑
      }
    } catch (error) {
      alert('发送消息失败: ' + error.message);
    }
  };

  const formatTimestamp = (timestamp) => {
    // 使用UTC时间显示，避免时区问题
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC'
    });
    const timeStr = date.toLocaleTimeString('zh-CN', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    });
    return `${dateStr} ${timeStr} UTC`;
  };

  const getChatTitle = () => {
    if (currentChat === 'public') {
      return 'ToxChat Room';
    }
    if (currentChat && currentChat.startsWith('note_')) {
      return 'Note Editor';
    }
    return `Chat with ${currentChat}`;
  };

  const isNoteEditor = () => {
    return currentChat && currentChat.startsWith('note_');
  };

  const getConnectionStatus = () => {
    return isConnected ? 'Connected' : 'Connecting...';
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear this conversation history? This will permanently delete all messages from both cloud and local storage and cannot be recovered.')) {
      // Clear local message records
      setMessages([]);
      
      // Here should call backend API to clear cloud records
      
      // Mock API call
      // clearChatHistory(currentChat);
    }
  };

  // 获取显示的消息（根据过滤设置进行过滤）
  const getFilteredMessages = () => {
    if (!filterSettingsRef.current.onlyMessageTag || !filterSettingsRef.current.tag) {
      // 如果没有启用标签过滤或没有设置标签，显示所有消息
      return messages;
    }
    
    // 只显示与设置标签相同的消息
    const targetTag = filterSettingsRef.current.tag;
    return messages.filter(message => {
      // 检查消息是否有自定义标签
      if (message.isCustomAES && message.customTag) {
        return message.customTag === targetTag;
      }
      // 检查消息内容中是否包含标签
      if (message.content.includes(`[${targetTag}]`)) {
        return true;
      }
      return false;
    });
  };

  const isPrivateChat = () => {
    return currentChat && currentChat !== 'public' && !currentChat.startsWith('note_');
  };

  // 如果是笔记编辑器，直接返回笔记编辑器组件
  if (isNoteEditor()) {
    return (
      <NoteEditor
        noteId={currentChat}
        userKeys={userKeys}
        serverPublicKey={serverPublicKey}
        onSaveNote={(noteId, title, content) => {
        }}
        onUpdateTitle={(noteId, title) => {
        }}
        onDeleteNote={(noteId) => {
          // 这里应该调用父组件的方法来更新笔记列表
        }}
      />
    );
  }

  return (
    <div className="chat-area" key={`chat-${layoutKey}`}>
      <div className="chat-header">
        <div className="chat-title">
          <h2>{getChatTitle()}</h2>
          <span className="connection-status">
            <span className={`status-indicator ${isConnected ? 'connected' : 'connecting'}`}></span>
            {getConnectionStatus()}
          </span>
        </div>
        <div className="chat-actions">
          {isPrivateChat() && (
            <button 
              className="clear-history-btn"
              onClick={handleClearHistory}
              title="Clear History"
            >
              🗑️ Clear
            </button>
          )}
        {currentChat === 'public' && (
          <div className="chat-info">
            <span className="online-status">
              <span className="app-name">ToxChat</span>
              <span className="separator"> • </span>
              <span className="status-text">Online</span>
            </span>
          </div>
        )}
        </div>
      </div>

      <div className="messages-container">
        {getFilteredMessages().map(message => (
          <div
            key={message.id}
            className={`message ${message.isOwn ? 'own' : ''} ${message.encrypted ? 'encrypted' : ''}`}
          >
            <div className="message-header">
              <span className="user-id">{message.userId}</span>
              <div className="message-meta">
                <span className="timestamp">{formatTimestamp(message.timestamp)}</span>
                {message.encrypted && (
                  <span className="encrypted-indicator" title="使用自定义AES加密">🔒</span>
                )}
              </div>
            </div>
            <div className="message-content">
              {message.content}
              {message.isCustomAES && message.customTag && (
                <span className="custom-tag"> [{message.customTag}]</span>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <MessageInput
        onSendMessage={handleSendMessage}
        disabled={!isConnected}
        currentChat={currentChat}
        onFilterChange={handleFilterChange}
      />
    </div>
  );
};

export default ChatArea;
